import { useEffect, useState } from "react";
import {
  Plus,
  RefreshCcw,
  Trash,
  User,
  Phone,
  Mail,
  Building2,
  CreditCard,
  ShieldCheck,
  CalendarDays,
  Hash,
} from "lucide-react";
import LoadingScreen from "../Utils/LoadingScreen";
import useBank from "../../Sevices/useBank";
import { useGlobalContext } from "../../GlobalContext";
import useDriver from "../../Sevices/Driver Api/useDriver";

export default function BankPage() {
  const { AdminId } = useGlobalContext();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const { banks, loading, fetchBanks, addDriverBank, deleteDriverBank } =
    useBank();

  const { drivers, fetchDrivers } = useDriver();
  const itemsPerPage = 15;

  const [form, setForm] = useState({
    driverCode: "",
    email: "",
    phone: "",
    bankName: "",
    bankHolderName: "",
    accountNumber: "",
    ifscCode: "",
    aadharNumber: "",
    panNumber: "",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleDriverSelect = (e) => {
    const selectedDriverCode = e.target.value;
    const selectedDriver = drivers.find(
      (driver) => driver.driverId === selectedDriverCode,
    );

    if (selectedDriver) {
      setForm({
        ...form,
        driverCode: selectedDriverCode,
        email: selectedDriver.email || "",
        phone: selectedDriver.phone || "",
      });
    } else {
      setForm({
        ...form,
        driverCode: "",
        email: "",
        phone: "",
      });
    }
  };

  const resetForm = () => {
    setForm({
      driverCode: "",
      email: "",
      phone: "",
      bankName: "",
      bankHolderName: "",
      accountNumber: "",
      ifscCode: "",
      aadharNumber: "",
      panNumber: "",
    });
  };

  const handleDeleteClick = (bank) => {
    setSelectedBank(bank);
    setDeleteOpen(true);
  };

  useEffect(() => {
    fetchBanks();
    fetchDrivers();
  }, []);

  const filtered = banks.filter(
    (bank) =>
      bank.first_name?.toLowerCase().includes(search.toLowerCase()) ||
      bank.last_name?.toLowerCase().includes(search.toLowerCase()) ||
      bank.driver_code?.toLowerCase().includes(search.toLowerCase()) ||
      bank.bank_name?.toLowerCase().includes(search.toLowerCase()) ||
      bank.account_no?.includes(search) ||
      bank.ifsc_code?.toLowerCase().includes(search.toLowerCase()) ||
      bank.pan_no?.toLowerCase().includes(search.toLowerCase()) ||
      bank.aadhar_no?.includes(search),
  );

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentData = filtered.slice(indexOfFirst, indexOfLast);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  const isFormValid = () => {
    return (
      form.driverCode.trim() !== "" &&
      form.email.trim() !== "" &&
      form.phone.trim() !== "" &&
      form.bankName.trim() !== "" &&
      form.bankHolderName.trim() !== "" &&
      form.accountNumber.trim() !== "" &&
      form.ifscCode.trim() !== "" &&
      form.aadharNumber.trim() !== "" &&
      form.panNumber.trim() !== ""
    );
  };
  const formatDateTime = (value) => {
    if (!value) return "N/A";
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return value;
    }
  };

  const InfoBadge = ({ children, color = "gray" }) => {
    const styles = {
      gray: "bg-gray-100 text-gray-700",
      blue: "bg-blue-100 text-blue-700",
      green: "bg-green-100 text-green-700",
      orange: "bg-orange-100 text-orange-700",
      red: "bg-red-100 text-red-700",
      purple: "bg-purple-100 text-purple-700",
    };

    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${styles[color]}`}
      >
        {children}
      </span>
    );
  };

  const maskAccountNumber = (value) => {
    if (!value) return "N/A";
    const str = String(value);
    if (str.length <= 4) return str;
    return `•••• ${str.slice(-4)}`;
  };

  const maskAadhar = (value) => {
    if (!value) return "N/A";
    const str = String(value);
    if (str.length <= 4) return str;
    return `••••••••${str.slice(-4)}`;
  };
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-semibold">Driver Bank Accounts</h1>
      </div>

      <div className="mb-4 flex justify-between items-center space-y-3 sm:space-y-0 sm:flex-row flex-col">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by bank or driver name..."
          className="w-full md:w-1/3 px-3 py-1 border rounded shadow-sm"
        />

        <div className="flex space-x-4">
          <button
            onClick={() => setOpen(true)}
            className="flex items-center space-x-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-1 rounded shadow"
          >
            <Plus size={16} /> <span>Add Bank Details</span>
          </button>

          <button
            onClick={fetchBanks}
            className="flex items-center space-x-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-1 rounded shadow"
            disabled={loading}
          >
            <RefreshCcw size={16} />{" "}
            <span>{loading ? "Refreshing..." : "Refresh"}</span>
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingScreen />
      ) : (
        <>
          {/* add bank details */}
          {open && (
            <div className="fixed inset-0 flex items-center justify-center bg-black/50 p-3 ">
              <div className="bg-white rounded-2xl shadow-sm w-full max-w-4xl h-[95vh]">
                <div className="bg-gradient-to-r from-orange-500 to-orange-500 px-8 py-4 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="bg-white/20 p-2 rounded-lg">
                        <Plus className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">
                          Add Driver Bank Details
                        </h2>
                        <p className="text-blue-100 text-sm">
                          Fill in the information below to add new bank account
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setOpen(false);
                        resetForm();
                      }}
                      className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-all flex justify-center h-10 w-10 bg-white/20"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                <div className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="border-l-4 border-blue-500 pl-4">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                          Driver Information
                        </h3>
                      </div>

                      <div className="space-y-4">
                        <div className="group relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Driver{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="driverCode"
                            value={form.driverCode}
                            onChange={handleDriverSelect}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg 
                                  focus:outline-none focus:ring-2 focus:ring-blue-500 
                                  focus:border-transparent transition-all 
                                  group-hover:border-gray-400 relative z-50 bg-white"
                          >
                            <option value="">Select a driver</option>
                            {drivers && drivers.length > 0 ? (
                              drivers.map((driver) => (
                                <option
                                  key={driver.id || driver.driverId}
                                  value={driver.driverId}
                                >
                                  {driver.first_name || driver.name}{" "}
                                  {driver.last_name}
                                </option>
                              ))
                            ) : (
                              <option value="" disabled>
                                No drivers available
                              </option>
                            )}
                          </select>
                        </div>

                        <div className="group">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email Address{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            placeholder="driver@example.com"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all group-hover:border-gray-400"
                          />
                        </div>

                        <div className="group">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Phone Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={form.phone}
                            onChange={handleChange}
                            placeholder="9876543210"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg  focus outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all group-hover:border-gray-400"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="border-l-4 border-green-500 pl-4">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                          Bank Information
                        </h3>
                      </div>

                      <div className="space-y-4">
                        <div className="group">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Bank Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="bankName"
                            value={form.bankName}
                            onChange={handleChange}
                            placeholder="State Bank of India"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all group-hover:border-gray-400"
                          />
                        </div>

                        <div className="group">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Account Holder Name{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="bankHolderName"
                            value={form.bankHolderName}
                            onChange={handleChange}
                            placeholder="Shivam Kumar"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all group-hover:border-gray-400"
                          />
                        </div>

                        <div className="group">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Account Number{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="accountNumber"
                            value={form.accountNumber}
                            onChange={handleChange}
                            placeholder="1234567890123456"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all group-hover:border-gray-400"
                          />
                        </div>

                        <div className="group">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            IFSC Code <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="ifscCode"
                            value={form.ifscCode}
                            onChange={handleChange}
                            placeholder="SBIN0001234"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all group-hover:border-gray-400"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="md:col-span-2 space-y-6">
                      <div className="border-l-4 border-purple-500 pl-4">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                          Document Information
                        </h3>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="group">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Aadhar Number{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="aadharNumber"
                            value={form.aadharNumber}
                            onChange={handleChange}
                            placeholder="1234 5678 9012"
                            maxLength="12"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all group-hover:border-gray-400"
                          />
                        </div>

                        <div className="group">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            PAN Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="panNumber"
                            value={form.panNumber}
                            onChange={handleChange}
                            placeholder="ABCDE1234F"
                            maxLength="10"
                            style={{ textTransform: "uppercase" }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all group-hover:border-gray-400"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="px-8 py-3 rounded-b-2xl">
                  <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
                    <button
                      onClick={() => {
                        setOpen(false);
                        resetForm();
                      }}
                      className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        const success = await addDriverBank(form);
                        if (success) {
                          setOpen(false);
                          resetForm();
                        }
                      }}
                      disabled={loading || !isFormValid()}
                      className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center justify-center space-x-2 ${
                        loading || !isFormValid()
                          ? "bg-gray-400 cursor-not-allowed opacity-50"
                          : "bg-gradient-to-r from-orange-500 to-orange-500 hover:from-orange-600 hover:to-orange-600 text-white"
                      } disabled:transform-none`}
                    >
                      Save Bank Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* delete bank details */}
          {deleteOpen && selectedBank && (
            <div className="fixed inset-0 flex items-center justify-center bg-black/20 z-50">
              <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-xl mt-10">
                <h2 className="text-lg font-semibold mb-4 text-red-500">
                  Delete Bank Details
                </h2>

                <div className="space-y-3">
                  <p className="mb-4 text-gray-700">
                    Are you sure you want to delete this bank detail?
                  </p>

                  <div className="bg-gray-100 rounded p-4">
                    <p className="mb-2">
                      <span className="font-medium">Driver Name: </span>
                      {selectedBank.first_name} {selectedBank.last_name}
                    </p>

                    <p className="mb-2">
                      <span className="font-medium">Driver Code: </span>
                      {selectedBank.driver_code}
                    </p>

                    <p className="mb-2">
                      <span className="font-medium">Bank Name: </span>
                      {selectedBank.bank_name}
                    </p>

                    <p className="mb-2">
                      <span className="font-medium">Account Number: </span>
                      {selectedBank.account_no}
                    </p>

                    <p className="mb-2">
                      <span className="font-medium">IFSC Code: </span>
                      {selectedBank.ifsc_code}
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3 mt-4">
                    <button
                      onClick={() => {
                        setDeleteOpen(false);
                        setSelectedBank(null);
                      }}
                      className="px-4 py-2 bg-gray-400 hover:0 text-white rounded"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        const success = await deleteDriverBank(selectedBank.id);
                        if (success) {
                          setDeleteOpen(false);
                          setSelectedBank(null);
                          resetForm();
                        }
                      }}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded"
                      disabled={loading}
                    >
                      {loading ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-orange-400 text-white border-b border-gray-200">
                  <tr className="text-left">
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                      Driver
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                      Contact
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                      Bank Details
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                      Documents
                    </th>
                    {AdminId === "3" || AdminId === "13" ? (
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-center">
                        Actions
                      </th>
                    ) : null}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100 bg-white">
                  {currentData.map((bank, index) => (
                    <tr
                      key={bank.id}
                      className="align-top transition-colors duration-200 hover:bg-orange-50/40"
                    >
                      {/* Serial */}

                      {/* Driver */}
                      <td className="px-4 py-3">
                        <div className="min-w-[220px] space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-orange-700">
                              <User size={18} />
                            </div>

                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {[bank.first_name, bank.last_name]
                                  .filter(Boolean)
                                  .join(" ") || "N/A"}
                              </p>

                              <div className="mt-1 flex flex-wrap gap-1.5">
                                <InfoBadge color="blue">
                                  <span className="inline-flex items-center gap-1">
                                    <Hash size={11} />
                                    {bank.driver_code || "N/A"}
                                  </span>
                                </InfoBadge>

                                <InfoBadge color="gray">
                                  ID #{bank.id}
                                </InfoBadge>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <CalendarDays
                              size={13}
                              className="shrink-0 text-gray-400"
                            />
                            <span>{formatDateTime(bank.date_created)}</span>
                          </div>
                        </div>
                      </td>

                      {/* Contact */}
                      <td className="px-4 py-3">
                        <div className="min-w-[220px] space-y-2 text-xs text-gray-700">
                          <div className="flex items-center gap-2 min-w-0">
                            <Mail
                              size={13}
                              className="shrink-0 text-gray-400"
                            />
                            <span className="truncate">
                              {bank.email || "N/A"}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 min-w-0">
                            <Phone
                              size={13}
                              className="shrink-0 text-gray-400"
                            />
                            <span className="truncate">
                              {bank.phone || "N/A"}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Bank Details */}
                      <td className="px-4 py-3">
                        <div className="min-w-[240px] space-y-2 text-xs text-gray-700">
                          <div className="flex items-center gap-2 min-w-0">
                            <Building2
                              size={13}
                              className="shrink-0 text-gray-400"
                            />
                            <span className="font-medium text-gray-900 truncate">
                              {bank.bank_name || "N/A"}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 min-w-0">
                            <CreditCard
                              size={13}
                              className="shrink-0 text-gray-400"
                            />
                            <span>
                              <span className="font-medium text-gray-500">
                                A/C:
                              </span>{" "}
                              {maskAccountNumber(bank.account_no)}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 min-w-0">
                            <ShieldCheck
                              size={13}
                              className="shrink-0 text-gray-400"
                            />
                            <span>
                              <span className="font-medium text-gray-500">
                                IFSC:
                              </span>{" "}
                              {bank.ifsc_code || "N/A"}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Documents */}
                      <td className="px-4 py-3">
                        <div className="min-w-[220px] space-y-2 text-xs text-gray-700">
                          <div className="flex flex-wrap gap-1.5">
                            <InfoBadge color="purple">
                              Aadhaar: {maskAadhar(bank.aadhar_no)}
                            </InfoBadge>
                          </div>

                          <div className="flex flex-wrap gap-1.5">
                            <InfoBadge color="green">
                              PAN: {bank.pan_no || "N/A"}
                            </InfoBadge>
                          </div>
                        </div>
                      </td>

                      {/* Actions */}
                      {AdminId === "3" || AdminId === "13" ? (
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1.5">
                            <button
                              onClick={() => handleDeleteClick(bank)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-red-600 transition hover:bg-red-50"
                              title="Delete Bank Details"
                            >
                              <Trash size={15} />
                            </button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {filtered.length === 0 && !loading && (
            <div className="text-center py-8 text-gray-500">
              No bank details found.
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-end items-center space-x-2 mt-4">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`px-3 py-1 rounded ${
                    currentPage === i + 1
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {i + 1}
                </button>
              ))}

              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
